import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import Button from '@ui/Button';
import { Divider, Form } from 'antd';
import { getSchema } from './schema';
import { yupResolver } from '@hookform/resolvers/yup';
import { Props } from './interfaces';
import { Row, Col } from 'antd';
import { default as ProgKidsForm } from '@ui/Form';
import {
  DatePickerFormField,
  InputFormField,
  TextAreaFormField,
  SelectFormField,
  CheckBoxFormField,
} from '@ui/Form/Fields';
import { EmployeeData } from '@/types/user';
import { I18n } from 'react-redux-i18n';
import { Option } from 'antd/lib/mentions';
import { FilePond, registerPlugin } from 'react-filepond';
import 'filepond/dist/filepond.min.css';
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';
import { userApi } from '@api/index';

registerPlugin(FilePondPluginFileValidateType);

export const EmployeeInfo: FC<Props> = ({
  userId,
  employee,
  onSubmit,
  isMyProfile,
}) => {
  const [uploading, setUploading] = useState(false);
  const [photo, setPhoto] = useState('');

  const formMethods = useForm<EmployeeData>({
    mode: 'all',
    reValidateMode: 'onChange',
    defaultValues: employee,
    resolver: yupResolver(getSchema()),
  });
  const {
    handleSubmit,
    reset,
    watch,
    formState: { isDirty },
  } = formMethods;

  const paymentMethod = watch('paymentMethod');
  const taxNumber = watch('taxNumber');

  const onProcessFile = (err, file) => {
    setPhoto(file.serverId.replace('""', ''));
    setUploading(false);
  };

  useEffect(() => {
    reset(employee);
  }, [employee, reset]);

  const handleSubmitSaveChanges = useCallback(async () => {
    handleSubmit(onSubmit)();
  }, [handleSubmit, onSubmit]);

  const checkTaxNumber = useCallback(async () => {
    if (!taxNumber) return alert('Указан некорректный ИНН');
    const result = await userApi.checkTaxNumber(taxNumber);
    alert(result.status);
  }, [taxNumber]);

  const pondRef = useRef(null);

  const handleClick = () => {
    (pondRef.current as any).browse();
  };

  useEffect(() => {
    if (employee) setPhoto((employee as any).photo);
  }, [employee]);

  if (!employee) return null;

  return (
    <ProgKidsForm formMethods={formMethods} onSubmit={handleSubmitSaveChanges}>
      <Row gutter={24}>
        <Col span={12}>
          {/* TODO: поменять местами фамилию и имя */}
          <InputFormField
            name="firstName"
            label={I18n.t('settings.employee.lastName')}
          />

          <InputFormField
            name="lastName"
            label={I18n.t('settings.employee.firstName')}
          />

          <InputFormField
            name="patronymic"
            label={I18n.t('settings.employee.patronymic')}
          />

          <InputFormField
            name="phone"
            label={I18n.t('settings.employee.phone')}
          />

          <InputFormField
            name="emergencyContact"
            label={I18n.t('settings.employee.emergencyContact')}
          />
        </Col>
        <Col span={12} style={{ textAlign: 'center' }}>
          <img src={photo} height={200} /> <br />
          {isMyProfile && (
            <button onClick={handleClick} type="button">
              {uploading ? 'Loading...' : 'Upload Photo'}
            </button>
          )}
          <div style={{ display: 'none' }}>
            <FilePond
              ref={pondRef}
              credits={false}
              onaddfile={() => setUploading(true)}
              onprocessfile={onProcessFile}
              allowMultiple={false}
              server={'/user/profile/photo'}
              maxFiles={1}
              name="files"
              acceptedFileTypes={['image/*']}
            />
          </div>
        </Col>
      </Row>

      <Row gutter={24}>
        <Col span={8}>
          <InputFormField name="mattermost" label="Mattermost" />
        </Col>
        <Col span={6}>
          <InputFormField name="tg" label="Telegram" />
        </Col>
        <Col span={6}>
          <DatePickerFormField
            name="birthDate"
            label={I18n.t('settings.employee.birthDate')}
            format="DD.MM.YYYY"
          />
        </Col>
        <Col span={4}>
          <SelectFormField
            label={I18n.t('settings.employee.sex')}
            name="sex"
            allowClear
          >
            <Option value="male">{I18n.t('settings.employee.male')}</Option>
            <Option value="female">{I18n.t('settings.employee.female')}</Option>
          </SelectFormField>
        </Col>
      </Row>

      <Row gutter={24}>
        <Col span={24}>
          <InputFormField
            name="education"
            label={I18n.t('settings.employee.education')}
          />
        </Col>
      </Row>

      <Row gutter={24}>
        <Col span={24}>
          <TextAreaFormField
            name="about"
            label={I18n.t('settings.employee.about')}
          />
        </Col>
      </Row>

      <Row>
        <Col span={12}>
          <CheckBoxFormField name="hidden">
            {I18n.t('settings.employee.hidden')} https://progkids.com
          </CheckBoxFormField>
        </Col>
        <Col span={12}>
          {I18n.t('settings.employee.tipsLink')}
          <a
            href={`https://app.progkids.com/i/tips/${userId}`}
          >{`https://app.progkids.com/i/tips/${userId}`}</a>
        </Col>
      </Row>

      <Divider />

      <Row gutter={24}>
        <Col span={10}>
          <SelectFormField
            label={I18n.t('settings.employee.paymentMethod')}
            name="paymentMethod"
          >
            <Option value="konsole">
              {I18n.t('settings.employee.konsole')}
            </Option>
            <Option value="4dev">{I18n.t('settings.employee.4dev')}</Option>
            <Option value="other">{I18n.t('settings.employee.other')}</Option>
          </SelectFormField>
        </Col>
        <Col span={12}>
          {paymentMethod === 'konsole' && (
            <InputFormField
              name="taxNumber"
              label={
                <>
                  {I18n.t('settings.employee.taxNumber')}
                  <button type="button" onClick={checkTaxNumber}>
                    Проверить ИНН
                  </button>
                </>
              }
            />
          )}

          {paymentMethod === 'other' && (
            <InputFormField
              name="paymentOther"
              label={I18n.t('settings.employee.other')}
            />
          )}
        </Col>
      </Row>

      <Form.Item wrapperCol={{ offset: 8, span: 16 }}>
        <Button
          color="green"
          size="big"
          onClick={handleSubmitSaveChanges}
          disabled={!isDirty}
        >
          {I18n.t('settings.employee.save')}
        </Button>
      </Form.Item>
    </ProgKidsForm>
  );
};
